import { supabase } from "@/lib/supabase";
import { supabaseAdmin } from "@/lib/supabaseAdmin";

/**
 * Check if a user is a member of a workspace
 * @param {string} userId - The user ID
 * @param {string} workspaceId - The workspace ID
 * @returns {Promise<boolean>} - Whether the user is a member
 */
export const isWorkspaceMember = async (userId, workspaceId) => {
  try {
    const { data, error } = await supabase
      .from("workspace_members")
      .select("id")
      .eq("user_id", userId)
      .eq("workspace_id", workspaceId)
      .single();

    if (error && error.code !== "PGRST116") {
      // PGRST116 is "not found" error
      console.error("Error checking workspace membership:", error);
      return false;
    }

    return !!data;
  } catch (error) {
    console.error("Error checking workspace membership:", error);
    return false;
  }
};

/**
 * Fetch shared thread data for a workspace member
 * @param {string} threadId - The thread ID
 * @param {string} userId - The user ID
 * @param {string} workspaceId - The workspace ID
 * @returns {Promise<Object|null>} - Thread data or null if not accessible
 */
export const fetchSharedThreadData = async (threadId, userId, workspaceId) => {
  try {
    // First check if user is a workspace member
    const isMember = await isWorkspaceMember(userId, workspaceId);
    if (!isMember) {
      return null;
    }

    // Fetch the thread data
    const { data: thread, error } = await supabase
      .from("threads")
      .select("*")
      .eq("id", threadId)
      .eq("workspace_id", workspaceId)
      .single();

    if (error) {
      console.error("Error fetching shared thread:", error);
      return null;
    }

    return thread;
  } catch (error) {
    console.error("Error in fetchSharedThreadData:", error);
    return null;
  }
};

/**
 * Update a thread to be shared in a workspace
 * @param {string} threadId - The thread ID
 * @param {string} workspaceId - The workspace ID
 * @returns {Promise<boolean>} - Success status
 */
export const updateThreadAsShared = async (threadId, workspaceId) => {
  try {
    const { error } = await supabase
      .from("threads")
      .update({
        workspace_id: workspaceId,
        sharing_enabled: true,
        updated_at: new Date().toISOString(),
      })
      .eq("id", threadId);

    if (error) {
      console.error("Error updating thread as shared:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error in updateThreadAsShared:", error);
    return false;
  }
};

/**
 * Fetches thread messages with admin privileges for workspace access
 * @param {string} openaiThreadId - The OpenAI thread ID
 * @param {string} userId - The current user's ID
 * @param {string} workspaceId - The workspace ID containing the thread
 * @returns {Promise<Array>} - The thread messages
 */
export const fetchSharedThreadMessages = async (
  openaiThreadId,
  userId,
  workspaceId
) => {
  try {
    // First check if user is workspace member
    const isMember = await isWorkspaceMember(userId, workspaceId);

    if (!isMember) {
      throw new Error("User is not a member of this workspace");
    }

    // Use admin client to bypass RLS and fetch the thread messages
    const { data: messages, error } = await supabaseAdmin
      .from("thread_messages")
      .select("*")
      .eq("thread_id", openaiThreadId)
      .order("created_at", { ascending: true });

    if (error) throw error;

    return messages;
  } catch (error) {
    console.error("Error fetching shared thread messages:", error);
    throw error;
  }
};
